import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { nowTime } from './format';
import type {
  BudgetView,
  BudgetPlanInput,
  Account,
  AccountTotals,
  AccountType,
  AdminAiConfig,
  AdminOverview,
  AdminUserDetail,
  AdminUserSummary,
  AgentLogEntry,
  AnalyticsDetail,
  Category,
  ChatMessage,
  EffectiveProvider,
  Insight,
  Investments,
  ProviderMeta,
  Settings,
  Summary,
  Transaction,
  TransactionPage,
  TransactionSplit,
} from './types';

/** One place for cache keys, so invalidation can't drift from the fetchers. */
export const keys = {
  summary: (range: string) => ['summary', range] as const,
  detail: ['analytics-detail'] as const,
  transactions: (filters: unknown) => ['transactions', filters] as const,
  categories: ['categories'] as const,
  accounts: ['accounts'] as const,
  budgets: ['budgets'] as const,
  insights: ['insights'] as const,
  chat: ['chat'] as const,
  logs: ['logs'] as const,
  investments: ['investments'] as const,
  settings: ['settings'] as const,
  devices: ['device-tokens'] as const,
  loans: ['loans'] as const,
  adminOverview: ['admin-overview'] as const,
  adminUsers: ['admin-users'] as const,
  adminUser: (id: string) => ['admin-users', id] as const,
  adminAiProviders: ['admin-ai-providers'] as const,
  adminAiConfig: ['admin-ai-config'] as const,
  adminAiModels: ['admin-ai-models'] as const,
};

/** Anything derived from transactions must refetch when they change. */
export function invalidateFinancials(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['analytics-period'] });
  void qc.invalidateQueries({ queryKey: ['sterling-score'] });
  void qc.invalidateQueries({ queryKey: ['friends'] });
  // Categories too: recording a transaction can mint a new AI-suggested
  // category, and the pickers need to show it without a reload.
  //
  // Accounts as well: a current balance is the opening figure plus the
  // account's transactions, so it is stale the moment any of them change —
  // including the cash legs a loan writes.
  for (const key of [
    ['ventures'],
    ['summary'],
    keys.detail,
    ['transactions'],
    keys.insights,
    keys.investments,
    keys.categories,
    keys.accounts,
    keys.budgets,
  ]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

export function useSummary(range: '1M' | '3M' | '12M') {
  return useQuery({
    queryKey: keys.summary(range),
    queryFn: () => api<Summary>(`/analytics/summary?range=${range}`),
  });
}

export function useAnalyticsDetail() {
  return useQuery({
    queryKey: keys.detail,
    queryFn: () => api<AnalyticsDetail>('/analytics/detail'),
  });
}

export type VentureChoice = { id: string; name: string; currency: string; archived: boolean };
export function useVentures() {
  return useQuery({ queryKey: ['ventures'], queryFn: () => api<{ ventures: VentureChoice[] }>('/ventures') });
}

export type TransactionFilters = {
  scope?: 'all' | 'personal' | 'business';
  ventureId?: string;
  q?: string;
  categoryIds?: string[];
  accountId?: string;
  from?: string;
  to?: string;
};

export function useTransactions(filters: TransactionFilters) {
  const params = new URLSearchParams();
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.ventureId) params.set('ventureId', filters.ventureId);
  if (filters.q) params.set('q', filters.q);
  if (filters.categoryIds?.length) params.set('categoryIds', filters.categoryIds.join(','));
  if (filters.accountId) params.set('accountId', filters.accountId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  params.set('limit', '200');

  return useQuery({
    queryKey: keys.transactions(filters),
    queryFn: () => api<TransactionPage>(`/transactions?${params.toString()}`),
    placeholderData: (prev) => prev, // keeps the table steady while filters change
  });
}

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: () => api<{ categories: Category[] }>('/categories').then((r) => r.categories),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: keys.accounts,
    queryFn: () => api<{ accounts: Account[]; totals: AccountTotals }>('/accounts'),
    // Balances are derived from transactions, so this can't be cached for long.
    staleTime: 15 * 1000,
  });
}

export type CreateTransactionInput = {
  ventureId?: string | null;
  ventureDirection?: 'IN' | 'OUT';
  accountId: string;
  categoryId: string | null;
  date: string;
  time?: string | null;
  merchant: string;
  note?: string;
  amount: number;
};

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) =>
      api<{ transaction: Transaction; createdCategory: { id: string; name: string } | null }>('/transactions', { method: 'POST', body: input }),
    onSuccess: () => invalidateFinancials(qc),
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<CreateTransactionInput>) =>
      api<{ transaction: Transaction }>(`/transactions/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateFinancials(qc),
  });
}

/** Account balance either side of one transaction — for tracing where a balance went off. */
export function useTransactionBalance(id: string) {
  return useQuery({
    // Under the 'transactions' prefix so any ledger change invalidates it.
    queryKey: ['transactions', 'balance', id] as const,
    queryFn: () => api<{ beforeCents: number; afterCents: number }>(`/transactions/${id}/balance`),
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/transactions/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFinancials(qc),
  });
}

// ── Splits (other people's shares of a payment) ─────────────────────────

export type SplitInput = { name: string; amount: number; friendUserId?: string };

/**
 * Adds people's shares to a payment. Each share becomes a claim under "owed to
 * you"; settling one writes the repayment into the ledger, so loans, balances
 * and the ledger all have to refetch.
 */
export function useAddSplits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      people,
      dueDate,
    }: {
      transactionId: string;
      people: SplitInput[];
      dueDate?: string | null;
    }) =>
      api<{ splits: TransactionSplit[] }>(`/transactions/${transactionId}/splits`, {
        method: 'POST',
        body: { people, dueDate: dueDate || null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
  });
}

export function useSettleSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      splitId,
      settled,
    }: {
      transactionId: string;
      splitId: string;
      settled: boolean;
    }) =>
      api<{ split: TransactionSplit }>(`/transactions/${transactionId}/splits/${splitId}`, {
        method: 'PATCH',
        body: { settled },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
    },
  });
}

export function useDeleteSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, splitId }: { transactionId: string; splitId: string }) =>
      api<void>(`/transactions/${transactionId}/splits/${splitId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
    },
  });
}

export function useSetBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, amount }: { categoryId: string; amount: number }) =>
      api(`/categories/budgets/${categoryId}`, { method: 'PUT', body: { amount } }),
    onSuccess: () => invalidateFinancials(qc),
  });
}

export function useInsights() {
  return useQuery({
    queryKey: keys.insights,
    queryFn: () => api<{ insights: Insight[] }>('/advisor/insights').then((r) => r.insights),
  });
}

export function useInsightAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'apply' | 'dismiss' }) =>
      api(`/advisor/insights/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.insights });
      void qc.invalidateQueries({ queryKey: keys.logs });
    },
  });
}

export function useChat() {
  return useQuery({
    queryKey: keys.chat,
    queryFn: () =>
      api<{ messages: ChatMessage[]; provider: { id: string; label: string; source: string } }>(
        '/advisor/messages',
      ),
  });
}

export type AskResponse = {
  message: ChatMessage;
  degraded: string | null;
  provider: { id: string; label: string; source: string };
};

export function useAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) =>
      api<AskResponse>('/advisor/ask', { method: 'POST', body: { message } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.chat });
    },
  });
}

// ── Loans (lent / borrowed) ─────────────────────────────────────────────

export type LoansResponse = {
  loans: import('./types').Loan[];
  summary: { lentOpenCents: number; borrowedOpenCents: number };
};

export type LoanInput = {
  friendUserId?: string;
  kind: 'LENT' | 'BORROWED';
  counterparty: string;
  amount: number;
  date?: string;
  dueDate?: string | null;
  note?: string;
  /** Explicit null records a pure IOU with no cash movement. */
  accountId?: string | null;
};

export function useLoans() {
  return useQuery({
    queryKey: keys.loans,
    queryFn: () => api<LoansResponse>('/loans'),
  });
}

export function useCreateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoanInput) =>
      api<{ loan: unknown }>('/loans', { method: 'POST', body: input }),
    // A loan writes real cash legs, so balances and the ledger move with it.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
    },
  });
}

export function useUpdateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; settled?: boolean } & Partial<LoanInput>) =>
      api<{ loan: unknown }>(`/loans/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
    },
  });
}

export function useDeleteLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/loans/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.loans });
      invalidateFinancials(qc);
    },
  });
}

/** Wipes the advisor conversation so the next question starts fresh —
 *  the server replays recent history into the prompt otherwise. */
export function useClearChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/advisor/messages', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.chat });
    },
  });
}

export function useAgentLogs() {
  return useQuery({
    queryKey: keys.logs,
    queryFn: () => api<{ logs: AgentLogEntry[] }>('/advisor/logs').then((r) => r.logs),
  });
}

export function useInvestments() {
  return useQuery({
    queryKey: keys.investments,
    queryFn: () => api<Investments>('/investments'),
  });
}

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api<{ settings: Settings; effective: EffectiveProvider }>('/settings'),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ settings: Settings; effective: EffectiveProvider }>('/settings', {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(keys.settings, data);
      void qc.invalidateQueries({ queryKey: ['sterling-score'] });
      // Advisor behaviour and insight thresholds both key off settings.
      void qc.invalidateQueries({ queryKey: keys.insights });
      void qc.invalidateQueries({ queryKey: keys.investments });
    },
  });
}

export type DeviceToken = {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
};

export function useDeviceTokens() {
  return useQuery({
    queryKey: keys.devices,
    queryFn: () => api<{ tokens: DeviceToken[] }>('/ingest/tokens').then((r) => r.tokens),
  });
}

export function useCreateDeviceToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{
        token: string;
        device: DeviceToken;
        shortcutPath: string;
        /** Base64 {endpoint, token} for the signed Setup shortcut to decode. */
        setupCode: string;
        /** Whether this server has CI-signed shortcut files to download. */
        signedShortcuts: boolean;
        /** Admin-provided iCloud share links; preferred over file downloads. */
        icloudLinks: { setup: string; log: string; picker: string | null } | null;
        warning: string;
      }>('/ingest/tokens', {
        method: 'POST',
        body: { name },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.devices }),
  });
}

export function useRevokeDeviceToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/ingest/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.devices }),
  });
}

/** Parse-only, for the share target to prefill the record form. */
export function useParseQuickEntry() {
  return useMutation({
    mutationFn: (text: string) =>
      api<{
        amount: number;
        merchant: string;
        note: string | null;
        date: string;
        isIncome: boolean;
      }>('/ingest/parse', { method: 'POST', body: { text } }),
  });
}

export function useQuickAdd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      api<{ transaction: Transaction; autoCategorized: boolean; summary: string }>(
        '/ingest/quick',
        {
          method: 'POST',
          body: { text, time: nowTime() },
        },
      ),
    onSuccess: () => invalidateFinancials(qc),
  });
}

// ── Admin portal ───────────────────────────────────────────────────────

export function useAdminOverview() {
  return useQuery({
    queryKey: keys.adminOverview,
    queryFn: () => api<AdminOverview>('/admin/overview'),
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => api<{ users: AdminUserSummary[] }>('/admin/users').then((r) => r.users),
  });
}

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: keys.adminUser(id),
    queryFn: () => api<AdminUserDetail>(`/admin/users/${id}`),
    enabled: Boolean(id),
  });
}

export function useAdminUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: 'USER' | 'ADMIN'; disabled?: boolean }) =>
      api<{ user: AdminUserSummary }>(`/admin/users/${id}`, { method: 'PATCH', body }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: keys.adminUsers });
      void qc.invalidateQueries({ queryKey: keys.adminUser(id) });
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
    },
  });
}

export function useAdminAiProviders() {
  return useQuery({
    queryKey: keys.adminAiProviders,
    queryFn: () =>
      api<{
        providers: ProviderMeta[];
        server: { envProvider: string; envConfigured: boolean; ollamaBaseUrl: string };
      }>('/admin/ai/providers'),
    staleTime: Infinity,
  });
}

export function useAdminAiConfig() {
  return useQuery({
    queryKey: keys.adminAiConfig,
    queryFn: () => api<{ config: AdminAiConfig; effective: EffectiveProvider }>('/admin/ai'),
  });
}

export function useAdminUpdateAiConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ config: AdminAiConfig; effective: EffectiveProvider }>('/admin/ai', {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(keys.adminAiConfig, data);
      void qc.invalidateQueries({ queryKey: keys.adminAiModels });
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      // Every user's "currently using" line reads this.
      void qc.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export type ModelList = {
  provider: string;
  label?: string;
  models: string[];
  current: string | null;
  currentIsValid?: boolean;
  message?: string;
};

/**
 * The system provider's live model catalogue. Fetched rather than hardcoded
 * because providers retire model IDs without warning.
 */
export function useAdminAvailableModels(enabled: boolean) {
  return useQuery({
    queryKey: keys.adminAiModels,
    queryFn: () => api<ModelList>('/admin/ai/models'),
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/** "Fetch models" in the admin AI card: lists with the typed key/address, before saving. */
export function useAdminFetchModels() {
  return useMutation({
    mutationFn: (body: { provider: string; apiKey?: string; baseUrl?: string }) =>
      api<{ provider: string; label: string; models: string[]; message?: string }>(
        '/admin/ai/models',
        {
          method: 'POST',
          body,
        },
      ),
  });
}

export function useAdminTestProvider() {
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; label: string; model?: string; message: string }>('/admin/ai/test', {
        method: 'POST',
      }),
  });
}

export function useCreateHolding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ticker: string;
      name: string;
      value: number;
      targetPct: number;
      gain?: number;
    }) => api('/investments', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.investments }),
  });
}

export function useUpdateHolding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api(`/investments/${id}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.investments }),
  });
}

export function useDeleteHolding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/investments/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.investments }),
  });
}

export type AccountInput = {
  name: string;
  type: AccountType;
  institution?: string | null;
  openingBalance?: number;
  openingDate?: string | null;
  isLiquid?: boolean;
  interestRate?: number;
};

/** Accounts feed net worth, so any change invalidates the financial views. */
function invalidateAccounts(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.accounts });
  void qc.invalidateQueries({ queryKey: ['summary'] });
  void qc.invalidateQueries({ queryKey: keys.investments });
  void qc.invalidateQueries({ queryKey: keys.insights });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountInput) =>
      api<{ account: Account }>('/accounts', { method: 'POST', body }),
    onSuccess: () => invalidateAccounts(qc),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<AccountInput>) =>
      api<{ account: Account }>(`/accounts/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateAccounts(qc),
  });
}

export function useArchiveAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAccounts(qc),
  });
}

export function useBudget(month: string) {
  return useQuery({
    queryKey: [...keys.budgets, month],
    queryFn: () => api<BudgetView>(`/budgets/${month}`),
  });
}
export function useBudgetMonths() {
  return useQuery({
    queryKey: [...keys.budgets, 'months'],
    queryFn: () => api<{ plans: Array<{ month: string; status: string }> }>('/budgets'),
  });
}
export function useSaveBudget(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BudgetPlanInput) =>
      api<BudgetView>(`/budgets/${month}`, { method: 'PUT', body }),
    onSuccess: (data) => {
      qc.setQueryData([...keys.budgets, month], data);
      invalidateFinancials(qc);
    },
  });
}
export function useSuggestBudget(month: string) {
  return useMutation({
    mutationFn: (body: { plan: BudgetPlanInput; method: 'ai' | 'history' | 'even'; notes?: string }) =>
      api<{
        allocations: BudgetPlanInput['allocations'];
        basis: 'ai' | 'history' | 'even';
        explanation?: string;
        provider?: { label: string; model: string };
        unallocatedCents: number;
        historyFrom: string;
        historyTo: string;
        historyAverageCents: Record<string, number>;
      }>(`/budgets/${month}/suggest`, { method: 'POST', body }),
  });
}
export function useCopyBudget(month: string) {
  return useMutation({
    mutationFn: () =>
      api<{ previous: Omit<BudgetPlanInput, 'version' | 'status'> }>(`/budgets/${month}/previous`),
  });
}
export function useTransferBudget(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      version: number;
      fromId: string;
      toId: string;
      amountCents: number;
      note: string;
    }) => api<BudgetView>(`/budgets/${month}/transfers`, { method: 'POST', body }),
    onSuccess: (data) => {
      qc.setQueryData([...keys.budgets, month], data);
      invalidateFinancials(qc);
    },
  });
}
export function useBudgetTransactions(month: string, categoryId: string | null) {
  return useQuery({
    queryKey: [...keys.budgets, month, 'transactions', categoryId],
    queryFn: () =>
      api<{
        count: number;
        transactions: Array<{
          id: string;
          date: string;
          merchant: string;
          amountCents: number;
          budgetSpendCents: number;
        }>;
      }>(
        `/budgets/${month}/transactions?categoryId=${encodeURIComponent(categoryId ?? 'uncategorized')}`,
      ),
  });
}
export function useCreateBudgetCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ category: Category }>('/categories', {
        method: 'POST',
        body: { name, kind: 'EXPENSE', color: 'oklch(0.78 0.12 190)' },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.categories });
    },
  });
}
